import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Search, Menu, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useState } from "react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

const LOGO_URL = "/logo.png";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop All" },
  { to: "/category/hair-care", label: "Hair Care" },
  { to: "/category/skin-care", label: "Skin Care" },
  { to: "/category/personal-hygiene", label: "Hygiene" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      {/* Announcement bar */}
      <div className="bg-roop-navy text-[#F9F6F0] text-xs md:text-sm overflow-hidden">
        <div className="whitespace-nowrap flex animate-marquee py-2">
          <span className="px-8">Free shipping on orders above ₹499</span>
          <span className="px-8">•</span>
          <span className="px-8">100% Ayurvedic • Handcrafted in India</span>
          <span className="px-8">•</span>
          <span className="px-8">Cash on Delivery available</span>
          <span className="px-8">•</span>
          <span className="px-8">Free shipping on orders above ₹499</span>
          <span className="px-8">•</span>
          <span className="px-8">100% Ayurvedic • Handcrafted in India</span>
          <span className="px-8">•</span>
          <span className="px-8">Cash on Delivery available</span>
        </div>
      </div>

      <header className="crystal-header sticky top-0 z-40" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <img src={LOGO_URL} alt="Roop Anand" className="h-14 md:h-16 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide uppercase transition-colors ${
                    isActive ? "text-[#E65A28]" : "text-[#0A192F] hover:text-[#E65A28]"
                  }`
                }
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-4">
            <button
              onClick={() => navigate("/shop")}
              className="hidden md:inline-flex p-2 rounded-full hover:bg-[#F4EBE1] transition-colors"
              data-testid="header-search-btn"
              aria-label="Search"
            >
              <Search className="h-5 w-5 text-[#0A192F]" />
            </button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-2 rounded-full hover:bg-[#F4EBE1] transition-colors" data-testid="user-menu-btn">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
                    ) : (
                      <User className="h-5 w-5 text-[#0A192F]" />
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-serif">Hi, {user.name?.split(" ")[0] || "there"}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/account")} data-testid="menu-my-account">My Account</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/account?tab=orders")} data-testid="menu-orders">My Orders</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/account?tab=addresses")} data-testid="menu-addresses">Address Book</DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">Admin Dashboard</DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout">Sign out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/signin"
                className="p-2 rounded-full hover:bg-[#F4EBE1] transition-colors"
                data-testid="signin-icon-btn"
                aria-label="Sign in"
              >
                <User className="h-5 w-5 text-[#0A192F]" />
              </Link>
            )}

            <Link
              to="/cart"
              className="relative p-2 rounded-full hover:bg-[#F4EBE1] transition-colors"
              data-testid="cart-btn"
              aria-label="Cart"
            >
              <ShoppingBag className="h-5 w-5 text-[#0A192F]" />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#E65A28] text-white text-[10px] font-semibold rounded-full h-5 w-5 flex items-center justify-center">
                  {count}
                </span>
              )}
            </Link>

            {/* Mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2 rounded-full hover:bg-[#F4EBE1]" data-testid="mobile-menu-btn" aria-label="Menu">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-[#F9F6F0]">
                <div className="flex flex-col gap-6 pt-6">
                  <img src={LOGO_URL} alt="Roop Anand" className="h-14 w-auto" />
                  {navLinks.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      onClick={() => setMobileOpen(false)}
                      className="font-serif text-2xl text-[#0A192F] hover:text-[#E65A28]"
                      data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </>
  );
}
